"use client"
import { useState, useEffect } from "react"
import axios from "axios"
import { ArrowUpward, ArrowDownward, Edit, Delete } from "@mui/icons-material"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from "@mui/material"
import AddSupplierModal from "../../components/Suppliers/AddSupplierModal"
import EditSupplierModal from "../../components/Suppliers/EditSupplierModal"
import AddSupplierGroupModal from "../../components/Suppliers/AddSupplierGroupModal"
import ConfirmationDialog from "../../components/Common/ConfirmationDialog"
import { useOutletContext } from "react-router-dom"
import BaseURL from "../../api/AppURL";

const SupplierManage = () => {
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [entriesPerPage, setEntriesPerPage] = useState(25)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const [downloading, setDownloading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [currentSupplierId, setCurrentSupplierId] = useState(null)
  const [addSupplierGroupModalOpen, setAddSupplierGroupModalOpen] = useState(false)

  // Confirmation Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [supplierToDeleteId, setSupplierToDeleteId] = useState(null)

  const { showSnackbar } = useOutletContext()

  const fetchSuppliers = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(`${BaseURL}/all/supplier`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      const fetchedData = Array.isArray(response.data.suppliers) ? response.data.suppliers : []
      setSuppliers(fetchedData)
      console.log("Suppliers fetched successfully. Total items:", fetchedData.length) // Debug log
    } catch (err) {
      console.error("Error fetching suppliers:", err)
      setError("Failed to load suppliers. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSuppliers()
  }, [])

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleEntriesChange = (event) => {
    setEntriesPerPage(event.target.value)
    setCurrentPage(1)
  }

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const sortedSuppliers = [...suppliers].sort((a, b) => {
    if (sortConfig.key === null) return 0

    let aValue = a[sortConfig.key]
    let bValue = b[sortConfig.key]

    if (sortConfig.key === "group") {
      aValue = a.group?.name || ""
      bValue = b.group?.name || ""
    }

    if (aValue < bValue) {
      return sortConfig.direction === "ascending" ? -1 : 1
    }
    if (aValue > bValue) {
      return sortConfig.direction === "ascending" ? 1 : -1
    }
    return 0
  })

  const filteredSuppliers = sortedSuppliers.filter((supplier) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      String(supplier.name).toLowerCase().includes(searchLower) ||
      String(supplier.mobile_no).toLowerCase().includes(searchLower) ||
      String(supplier.address).toLowerCase().includes(searchLower) ||
      String(supplier.group?.name || "")
        .toLowerCase()
        .includes(searchLower)
    )
  })

  const indexOfLastSupplier = currentPage * entriesPerPage
  const indexOfFirstSupplier = indexOfLastSupplier - entriesPerPage
  const displayedSuppliers = filteredSuppliers.slice(indexOfFirstSupplier, indexOfLastSupplier)
  const totalPages = Math.ceil(filteredSuppliers.length / entriesPerPage)

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null
    return sortConfig.direction === "ascending" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const totalEntries = filteredSuppliers.length
  const startEntry = totalEntries === 0 ? 0 : indexOfFirstSupplier + 1
  const endEntry = Math.min(indexOfLastSupplier, totalEntries)

  const handleDownloadPdf = async () => {
    setDownloading(true)
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(`${BaseURL}/all/supplier/report`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf",
        },
        responseType: "blob",
      })

      const contentDisposition = response.headers["content-disposition"]
      console.log("Content-Disposition Header:", contentDisposition)
      let filename = "suppliers_report.pdf"
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1]
        } else {
          const unquotedFilenameMatch = contentDisposition.match(/filename=([^;]+)/)
          if (unquotedFilenameMatch && unquotedFilenameMatch[1]) {
            filename = unquotedFilenameMatch[1].trim()
          }
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSnackbar("PDF report downloaded successfully!", "success")
    } catch (err) {
      console.error("Error downloading PDF:", err)
      showSnackbar("Failed to download PDF report. Please try again.", "error")
    } finally {
      setDownloading(false)
    }
  }

  const renderPageNumbers = () => {
    const pageNumbers = []
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <Button
          key={i}
          variant={currentPage === i ? "contained" : "outlined"}
          color={currentPage === i ? "primary" : "inherit"}
          onClick={() => setCurrentPage(i)}
          sx={{ minWidth: 40, px: 1 }}
        >
          {i}
        </Button>,
      )
    }
    return pageNumbers
  }

  const handleAddSupplierClick = () => {
    setAddModalOpen(true)
  }

  const handleAddModalClose = () => {
    setAddModalOpen(false)
  }

  const handleSupplierAddedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchSuppliers()
  }

  const handleEditSupplierClick = (supplierId) => {
    setCurrentSupplierId(supplierId)
    setEditModalOpen(true)
  }

  const handleEditModalClose = () => {
    setEditModalOpen(false)
    setCurrentSupplierId(null)
  }

  const handleSupplierUpdatedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchSuppliers()
  }

  // Open confirmation dialog
  const handleDeleteClick = (supplierId) => {
    setSupplierToDeleteId(supplierId)
    setConfirmDialogOpen(true)
  }

  // Close confirmation dialog
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false)
    setSupplierToDeleteId(null)
  }

  // Execute delete after confirmation
  const handleConfirmDelete = async () => {
    if (!supplierToDeleteId) return

    setConfirmDialogOpen(false) // Close the dialog immediately
    console.log("Attempting to delete supplier with ID:", supplierToDeleteId) // Debug log

    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.delete(`${BaseURL}/delete/supplier/${supplierToDeleteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      if (response.data.success === true) {
        showSnackbar(response.data.message || "Supplier deleted successfully!", "success")
        console.log("Supplier deleted successfully. Optimistically updating UI...") // Debug log

        // Optimistically remove the deleted supplier from the local state
        setSuppliers((prevSuppliers) => prevSuppliers.filter((s) => s.id !== supplierToDeleteId))

        // Then, re-fetch the full list to ensure consistency with the backend
        await fetchSuppliers()
        console.log("Table re-synced with backend after deletion.") // Debug log
      } else {
        showSnackbar(response.data.message || "Failed to delete supplier.", "error")
        console.log("Failed to delete supplier:", response.data.message) // Debug log
      }
    } catch (err) {
      console.error("Error deleting supplier:", err)
      showSnackbar("An error occurred while deleting the supplier.", "error")
    } finally {
      setSupplierToDeleteId(null) // Reset the ID
    }
  }

  const handleAddSupplierGroupClick = () => {
    setAddSupplierGroupModalOpen(true)
  }

  const handleAddSupplierGroupModalClose = () => {
    setAddSupplierGroupModalOpen(false)
  }

  const handleSupplierGroupAddedSuccess = (message) => {
    showSnackbar(message, "success")
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Action Buttons Section */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={handleAddSupplierClick}>
              Add Supplier
            </Button>
            <Button variant="outlined" onClick={handleDownloadPdf} disabled={downloading}>
              {downloading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
              All Supplier
            </Button>
            <Button variant="contained" onClick={handleAddSupplierGroupClick}>
              Add Supplier Group
            </Button>
          </Box>

          {/* Search and Entries Per Page Section */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Show</Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={entriesPerPage}
                    onChange={handleEntriesChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Entries per page" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2">entries</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                sx={{ ml: { sm: "auto" } }}
              />
            </Grid>
          </Grid>

          {/* Table Section */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Box sx={{ overflowX: "auto", minWidth: 650 }}>
                <Table aria-label="supplier table">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", width: "5%", borderRight: "1px solid #e0e0e0" }}>
                        Sl
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "25%", borderRight: "1px solid #e0e0e0" }}>
                        <Button onClick={() => requestSort("name")} sx={{ textTransform: "none", p: 0, minWidth: 0 }}>
                          Supplier Name {getSortIcon("name")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", borderRight: "1px solid #e0e0e0" }}>
                        <Button onClick={() => requestSort("group")} sx={{ textTransform: "none", p: 0, minWidth: 0 }}>
                          Group {getSortIcon("group")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "15%", borderRight: "1px solid #e0e0e0" }}>
                        <Button
                          onClick={() => requestSort("mobile_no")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                        >
                          Mobile No {getSortIcon("mobile_no")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "30%", borderRight: "1px solid #e0e0e0" }}>
                        <Button
                          onClick={() => requestSort("address")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                        >
                          Address {getSortIcon("address")}
                        </Button>
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: "10%" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedSuppliers.length > 0 ? (
                      displayedSuppliers.map((supplier, index) => (
                        <TableRow key={supplier.id || index}>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                            {indexOfFirstSupplier + index + 1}
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{supplier.name}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{supplier.group?.name}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{supplier.mobile_no}</TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{supplier.address}</TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleEditSupplierClick(supplier.id)}
                              >
                                <Edit fontSize="small" />
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteClick(supplier.id)}
                              >
                                <Delete fontSize="small" />
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 4 }}>
                          No suppliers found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </TableContainer>
          )}

          {/* Pagination Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "center", sm: "center" },
              gap: 2,
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handlePreviousPage} disabled={currentPage === 1}>
                Prev
              </Button>
              {renderPageNumbers()}
              <Button variant="outlined" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Add Supplier Modal */}
      <AddSupplierModal open={addModalOpen} onClose={handleAddModalClose} onSaveSuccess={handleSupplierAddedSuccess} />

      {/* Edit Supplier Modal */}
      {editModalOpen && (
        <EditSupplierModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          onUpdateSuccess={handleSupplierUpdatedSuccess}
          supplierId={currentSupplierId}
        />
      )}

      {/* Add Supplier Group Modal */}
      <AddSupplierGroupModal
        open={addSupplierGroupModalOpen}
        onClose={handleAddSupplierGroupModalClose}
        onSaveSuccess={handleSupplierGroupAddedSuccess}
      />

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        message="Delete This Data?"
        confirmText="Yes, delete it!"
        cancelText="Cancel"
      />
    </Box>
  )
}

export default SupplierManage
